import { createSlice } from "@reduxjs/toolkit";
import { createSelector } from "reselect";
import { portalApi } from "../api/portalApi";

// Re-export RTK Query hooks for direct component use
export {
  useGetChangeOrderDetailsQuery,
  usePostChangeOrderDetailMutation,
} from '../api/portalApi';

/**
 * Backward-compatible wrapper for postChangeOrderDetail.
 */
export const postChangeOrderDetail = (payload) => (dispatch) => {
  return dispatch(portalApi.endpoints.postChangeOrderDetail.initiate(payload));
};

/**
 * Backward-compatible wrapper for getChangeOrderDetail.
 */
export const getChangeOrderDetail = (projectID) => (dispatch, getState) => {
  const jobId = projectID ?? getState()?.jobId?.jobId;
  if (!jobId) {
    console.warn("No job ID available for change order detail fetch");
    return;
  }
  return dispatch(
    portalApi.endpoints.getChangeOrderDetails.initiate(jobId, {
      forceRefetch: true,
    })
  );
};

// Slice
const postChangeOrderDetailSlice = createSlice({
  name: "postChangeOrderDetail",
  initialState: {
    changeOrderDetails: [],
    status: "idle",
    error: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      // ── RTK Query: postChangeOrderDetail ────────────────────────────────
      .addMatcher(
        portalApi.endpoints.postChangeOrderDetail.matchPending,
        (state) => { state.status = "loading"; }
      )
      .addMatcher(
        portalApi.endpoints.postChangeOrderDetail.matchFulfilled,
        (state) => {
          state.status = "succeeded";
          state.error = null;
        }
      )
      .addMatcher(
        portalApi.endpoints.postChangeOrderDetail.matchRejected,
        (state, action) => {
          state.status = "failed";
          state.error = action.error?.message ?? 'Failed to post change order detail';
        }
      )
      // ── RTK Query: getChangeOrderDetails ────────────────────────────────
      .addMatcher(
        portalApi.endpoints.getChangeOrderDetails.matchPending,
        (state) => { state.status = "loading"; }
      )
      .addMatcher(
        portalApi.endpoints.getChangeOrderDetails.matchFulfilled,
        (state, action) => {
          state.status = "succeeded";
          state.changeOrderDetails = action.payload;
          state.error = null;
        }
      )
      .addMatcher(
        portalApi.endpoints.getChangeOrderDetails.matchRejected,
        (state, action) => {
          state.status = "failed";
          state.error = action.error?.message ?? 'Failed to fetch change order details';
        }
      );
  },
});

// Selectors
export const selectChangeOrderDetails = createSelector(
  (state) => state.postChangeOrderDetail.changeOrderDetails,
  (state) => state.jobId.jobId,
  (changeOrderDetails, jobId) =>
    changeOrderDetails.filter((detail) => detail.ProjectID === jobId)
);

export default postChangeOrderDetailSlice.reducer;
